<template>
  <WdsModal
    :show
    :lg="true"
    :title="webhookEventLogExtracted?.webhookEvent?.description + ' - ' + $t('Webhook Event Log Details')"
    class="modal-webhook-event-log-details"
    @cancel="show = false"
  >
    <WdsText primary large class="tw-mb-1">{{ $t('Webhook information') }}</WdsText>
    <CodeWrapper :header="$t('Webhook')" :content="webhookInfo(webhookExtracted)" />
    <CodeWrapper :header="$t('Webhook event')" :content="webhookEventInfo(webhookEventExtracted)" />
    <CodeWrapper :header="$t('Webhook event payload')" :content="webhookEventPayload(webhookEventExtracted)" />
    <CodeWrapper :header="$t('Webhook sending attempt')" :content="webhookEventLogInfo(webhookEventLogExtracted)" />

    <WdsText primary large class="tw-mb-1">{{ $t('Webhook response body') }} ({{ applicationType }})</WdsText>
    <CodeWrapper :header="$t('Formatted body from the response received from you')" :content="responseBodyFormatted" />
    <CodeWrapper :header="$t('Unformatted body from the response received from you')" :content="responseBody" />

    <WdsText primary large class="tw-mb-1">{{ $t('Webhook response') }} (JSON)</WdsText>
    <CodeWrapper :header="$t('Response details: This is what we received from you')" :content="responseDetails" />

    <WdsText primary large class="tw-mb-1">{{ $t('Webhook request') }} (JSON)</WdsText>
    <CodeWrapper :header="$t('Request details: This is what we sent to you')" :content="requestDetails" />
  </WdsModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { WdsModal, WdsText } from '@worksome/wds-components'
import escape from 'core-js/es/escape'
import moment from 'moment'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FragmentType, useFragment } from '@/gql'
import { WebhookEventLogFragment } from '@/components/Integrations/Webhooks/graphql/fragments'
import { textHelpers } from '@/utils/tools'
import CodeWrapper from '@/components/Integrations/Webhooks/CodeWrapper.vue'

const show = defineModel<boolean>('show')

const webhookEventLog = defineModel<FragmentType<typeof WebhookEventLogFragment>>('details')

const webhookExtracted = computed(() => useFragment(WebhookEventLogFragment, webhookEventLog.value)?.webhook)
const webhookEventExtracted = computed(() => useFragment(WebhookEventLogFragment, webhookEventLog.value)?.webhookEvent)
const webhookEventLogExtracted = computed(() => useFragment(WebhookEventLogFragment, webhookEventLog.value))

const webhookInfo = (webhookData) => `${webhookData.title} by ${webhookData.owner}\n${webhookData.url}`
const webhookEventInfo = (webhookEventData) => `${webhookEventData.description}: Created at ${moment(webhookEventData.createdAt).format('Y-MM-DD HH:mm:ss')}`
const webhookEventPayload = (webhookEventData) => JSON.stringify(JSON.parse(webhookEventData.payload), null, 2)
const webhookEventLogInfo = (webhookEventLogData) => `${textHelpers.capitalizeFirstLetter(webhookEventLogData.result)} (${webhookEventLogData.statusCode ?? 'N/A'}): Sent at ${moment(webhookEventLogData.createdAt).format('Y-MM-DD HH:mm:ss')}`

const decodeBody = (body: string) => {
  if (! body) {
    return ''
  }

  try {
    return decodeURIComponent(escape(window.atob(body)))
  } catch (e) {
    return '"❌ Body is not a valid base64 encoded string"'
  }
}

const getApplicationType = (headers: { [key: string]: string[] } | null) => {
  if (! headers) {
    return 'N/A'
  }

  const contentType = headers['Content-Type']
    ?.find((header: string) => header.indexOf('application/') === 0)
    ?? 'application/unknown'

  return contentType
    .split(';')[0]
    .split('/')[1]
    .toUpperCase()
    .trim()
}

const prettifyXml = (sourceXml: string) => {
  const xmlDoc = new DOMParser().parseFromString(sourceXml, 'application/xml')

  const xsltDoc = new DOMParser().parseFromString(`
    <xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform">
      <xsl:strip-space elements="*"/>
      <xsl:template match="para[content-style][not(text())]">
        <xsl:value-of select="normalize-space(.)"/>
      </xsl:template>
      <xsl:template match="node()|@*">
        <xsl:copy><xsl:apply-templates select="node()|@*"/></xsl:copy>
      </xsl:template>
      <xsl:output indent="yes"/>
    </xsl:stylesheet>`,
    'application/xml'
  )

  const xsltProcessor = new XSLTProcessor()

  xsltProcessor.importStylesheet(xsltDoc)

  const resultDoc = xsltProcessor.transformToDocument(xmlDoc)

  return new XMLSerializer().serializeToString(resultDoc)
}

const prettyResponse = (response: { body: string; headers: { [key: string]: string[] } }) => {
  const body = decodeBody(response.body)

  if (body === '') {
    return ''
  }

  const contentType = getApplicationType(response.headers)

  switch (contentType) {
    case 'JSON':
      return JSON.stringify(JSON.parse(body), null, 2)
    case 'XML':
      return prettifyXml(body)
  }

  return body
}

const responseHeaders = computed(() => JSON.parse(webhookEventLogExtracted.value?.response ?? '{}').headers)
const responseBody = computed(() => decodeBody(JSON.parse(webhookEventLogExtracted.value?.response ?? '{}').body))
const responseBodyFormatted = computed(() => prettyResponse(JSON.parse(webhookEventLogExtracted.value?.response ?? '{}')))
const responseDetails = computed(() => JSON.stringify(JSON.parse(webhookEventLogExtracted.value?.response ?? '{}'), null, 2))
const requestDetails = computed(() => JSON.stringify(JSON.parse(webhookEventLogExtracted.value?.request ?? '{}'), null, 2))

const applicationType = computed(() => getApplicationType(responseHeaders.value))
</script>

<style scoped lang="scss">
.modal-webhook-event-log-details {
  @apply tw-pt-4;

  :deep(.wds-modal-body) {
    @apply tw-mt-1;
  }
}
</style>
